import { useCallback, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import {
  Alert,
  Accordion,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import type {
  LegacyReportCropInput,
  LegacyReportManifest,
  LegacyReportDraftData,
  LegacyReportReviewAsset,
} from "../../../types/legacyReportReview";
import LegacyReportCropSelector from "./LegacyReportCropSelector";

interface Props {
  manifest: LegacyReportManifest;
  draft: LegacyReportDraftData;
  assets: LegacyReportReviewAsset[];
  dirty: boolean;
  onSave: (input: LegacyReportCropInput) => Promise<boolean>;
}

interface CropValues {
  pageNumber: string;
  x: number | string;
  y: number | string;
  width: number | string;
  height: number | string;
  role: "organMap" | "signature";
  signatureId: string | null;
  afterSectionId: string | null;
}

export default function LegacyReportCropModal({
  manifest,
  draft,
  assets,
  dirty,
  onSave,
}: Props) {
  const [opened, setOpened] = useState(false);
  const [pending, setPending] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [sourceReady, setSourceReady] = useState(false);
  const [selectionPending, setSelectionPending] = useState(false);
  const pageSelections = useRef<
    Record<string, Pick<CropValues, "x" | "y" | "width" | "height">>
  >({});
  const form = useForm<CropValues>({
    initialValues: {
      pageNumber: String(manifest.pages[0]?.pageNumber || 1),
      x: 0,
      y: 0,
      width: manifest.pages[0]?.width || 100,
      height: manifest.pages[0]?.height || 100,
      role: "organMap",
      signatureId: "",
      afterSectionId: "",
    },
    validateInputOnBlur: true,
    validate: {
      x: (value: number | string, values: CropValues) =>
        value !== "" &&
        Number(value) >= 0 &&
        Number(value) + Number(values.width) <=
          (manifest.pages.find(
            (page) => page.pageNumber === Number(values.pageNumber)
          )?.width || 0)
          ? null
          : "Keep the crop within the source page width.",
      y: (value: number | string, values: CropValues) =>
        value !== "" &&
        Number(value) >= 0 &&
        Number(value) + Number(values.height) <=
          (manifest.pages.find(
            (page) => page.pageNumber === Number(values.pageNumber)
          )?.height || 0)
          ? null
          : "Keep the crop within the source page height.",
      width: (value: number | string) =>
        Number(value) > 0 ? null : "Enter a positive width.",
      height: (value: number | string) =>
        Number(value) > 0 ? null : "Enter a positive height.",
      signatureId: (value: string | null, values: CropValues) =>
        values.role !== "signature" || value
          ? null
          : "Select the printed attribution for this signature crop.",
    },
  });
  const pageOptions = useMemo(
    () =>
      manifest.pages.map((page) => ({
        value: String(page.pageNumber),
        label: `Page ${page.pageNumber}`,
      })),
    [manifest.pages]
  );
  const selectedPage = useMemo(
    () =>
      manifest.pages.find(
        (page) => page.pageNumber === Number(form.values.pageNumber)
      ),
    [manifest.pages, form.values.pageNumber]
  );
  const selectedAsset = useMemo(
    () => assets.find((asset) => asset.id === selectedPage?.assetId),
    [assets, selectedPage?.assetId]
  );
  const selectedRegion = useMemo(
    () => ({
      x: Number(form.values.x),
      y: Number(form.values.y),
      width: Number(form.values.width),
      height: Number(form.values.height),
    }),
    [form.values.x, form.values.y, form.values.width, form.values.height]
  );
  const sectionOptions = useMemo(
    () =>
      draft.sections.map((section, index) => ({
        value: section.id,
        label: `${index + 1}. ${section.label || "Historical section"}`,
      })),
    [draft.sections]
  );
  const signatureOptions = useMemo(
    () =>
      draft.signatures.map((signature, index) => ({
        value: signature.id,
        label: `Attribution ${index + 1} · page ${
          signature.sourcePage || "unknown"
        }`,
      })),
    [draft.signatures]
  );
  const handleOpen = useCallback(() => {
    setSourceReady(false);
    setSelectionPending(false);
    setOpened(true);
  }, []);
  const handleSourceReady = useCallback(
    (ready: boolean) => setSourceReady(ready),
    []
  );
  const handleSelectionPending = useCallback(
    (active: boolean) => setSelectionPending(active),
    []
  );
  const handleRegionChange = useCallback(
    (region: LegacyReportCropInput["region"]) => {
      form.setValues(region);
      form.clearErrors();
      setSaveFailed(false);
    },
    [form]
  );
  const handlePageChange = useCallback(
    (value: string | null) => {
      if (!value || value === form.getValues().pageNumber) return;
      const page = manifest.pages.find(
        (item) => item.pageNumber === Number(value)
      );
      if (!page) return;
      const current = form.getValues();
      pageSelections.current[current.pageNumber] = {
        x: current.x,
        y: current.y,
        width: current.width,
        height: current.height,
      };
      form.setValues({
        pageNumber: value,
        ...(pageSelections.current[value] || {
          x: 0,
          y: 0,
          width: page.width,
          height: page.height,
        }),
      });
      form.clearErrors();
      setSourceReady(false);
      setSelectionPending(false);
    },
    [form, manifest.pages]
  );
  const handleClose = useCallback(() => {
    if (!pending) setOpened(false);
  }, [pending]);
  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (
        pending ||
        dirty ||
        !sourceReady ||
        selectionPending ||
        !selectedAsset
      )
        return;
      const validation = form.validate();
      if (validation.hasErrors) {
        form.getInputNode(Object.keys(validation.errors)[0])?.focus();
        return;
      }
      const values = form.getValues();
      setPending(true);
      setSaveFailed(false);
      try {
        const saved = await onSave({
          pageNumber: Number(values.pageNumber),
          region: {
            x: Number(values.x),
            y: Number(values.y),
            width: Number(values.width),
            height: Number(values.height),
          },
          role: values.role,
          ...(values.signatureId && values.role === "signature"
            ? { signatureId: values.signatureId }
            : {}),
          ...(values.afterSectionId
            ? { afterSectionId: values.afterSectionId }
            : {}),
        });
        if (saved) setOpened(false);
        else setSaveFailed(true);
      } finally {
        setPending(false);
      }
    },
    [pending, dirty, sourceReady, selectionPending, selectedAsset, form, onSave]
  );
  return (
    <>
      <Button variant="default" onClick={handleOpen}>
        Define source graphic boundaries
      </Button>
      <Modal
        opened={opened}
        onClose={handleClose}
        title="Preserve a source graphic region"
        centered
        size="xl"
        closeOnClickOutside={false}
      >
        <form onSubmit={handleSubmit} noValidate>
          <Stack gap="md">
            {saveFailed && (
              <Alert color="red" title="Source crop not saved">
                Your crop settings are retained. Close this dialog to review the
                request error, then try again.
              </Alert>
            )}
            {dirty && (
              <Alert color="orange">
                Save the current draft before creating a source graphic.
              </Alert>
            )}
            <Select
              label="Source page"
              required
              data={pageOptions}
              allowDeselect={false}
              disabled={pending}
              {...form.getInputProps("pageNumber")}
              onChange={handlePageChange}
            />
            {opened && selectedPage && selectedAsset ? (
              <LegacyReportCropSelector
                key={selectedAsset.id}
                page={selectedPage}
                asset={selectedAsset}
                region={selectedRegion}
                disabled={pending || dirty}
                onChange={handleRegionChange}
                onReadyChange={handleSourceReady}
                onPendingChange={handleSelectionPending}
              />
            ) : (
              <Alert color="red" title="Source page evidence unavailable">
                Reload the saved draft to load its preserved page images before
                creating a crop.
              </Alert>
            )}
            <Accordion variant="contained">
              <Accordion.Item value="precision">
                <Accordion.Control>
                  Precision adjustments (optional)
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <Text size="sm" c="dimmed">
                      Values are measured from the page's top-left corner in PDF
                      points. Your selection stays visible above.
                    </Text>
                    <SimpleGrid cols={2}>
                      <NumberInput
                        label="Left (x)"
                        required
                        min={0}
                        disabled={pending || selectionPending}
                        {...form.getInputProps("x")}
                      />
                      <NumberInput
                        label="Top (y)"
                        required
                        min={0}
                        disabled={pending || selectionPending}
                        {...form.getInputProps("y")}
                      />
                      <NumberInput
                        label="Width"
                        required
                        min={1}
                        disabled={pending || selectionPending}
                        {...form.getInputProps("width")}
                      />
                      <NumberInput
                        label="Height"
                        required
                        min={1}
                        disabled={pending || selectionPending}
                        {...form.getInputProps("height")}
                      />
                    </SimpleGrid>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
            <Select
              label="Graphic role"
              required
              data={[
                { value: "organMap", label: "Organ map and legend" },
                { value: "signature", label: "Signature" },
              ]}
              allowDeselect={false}
              disabled={pending}
              {...form.getInputProps("role")}
            />
            {form.values.role === "signature" && (
              <Select
                label="Printed historical attribution"
                required
                data={signatureOptions}
                disabled={pending}
                {...form.getInputProps("signatureId")}
              />
            )}
            <Select
              label="Place after section"
              clearable
              data={sectionOptions}
              disabled={pending}
              {...form.getInputProps("afterSectionId")}
            />
            <Group justify="space-between">
              <Button variant="default" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={pending}
                disabled={
                  !sourceReady || selectionPending || dirty || !selectedAsset
                }
              >
                Save verified source crop
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
